import React, { useState, useEffect } from 'react';
import { Link } from 'react-router-dom';
import { 
  ArrowRight, Phone, Calendar, CheckCircle, Star, MapPin,
  User, Users, Stethoscope, Shield, Award, Heart, ExternalLink
} from 'lucide-react';
import { siteConfig, clinician, verifiedStats, googleReviews, simplePractice } from '../data/content';
import BookingModal from '../components/common/BookingModal';

const HomePage = () => {
  const [showBookingModal, setShowBookingModal] = useState(false);
  const [prefersReducedMotion, setPrefersReducedMotion] = useState(false);
  const [videoLoaded, setVideoLoaded] = useState(false);

  // Check for prefers-reduced-motion
  useEffect(() => {
    const mediaQuery = window.matchMedia('(prefers-reduced-motion: reduce)');
    setPrefersReducedMotion(mediaQuery.matches);
    
    const handler = (e) => setPrefersReducedMotion(e.matches);
    mediaQuery.addEventListener('change', handler);
    return () => mediaQuery.removeEventListener('change', handler);
  }, []);

  return (
    <div className="min-h-screen">
      {/* Hero Section - Compact Split-Screen Layout */}
      <section className="pt-20 pb-4 md:pt-24 md:pb-6 bg-slate-50">
        <div className="container">
          <div className="grid lg:grid-cols-10 gap-4 lg:gap-6 items-center">
            
            {/* Left: Content (35%) */}
            <div className="lg:col-span-3">
              {/* Location Badge */}
              <div className="mb-2">
                <span className="inline-flex items-center gap-1.5 text-teal-700 text-xs font-medium tracking-wide uppercase">
                  <MapPin size={12} />
                  Greater Houston • Mobile Services
                </span>
              </div>

              {/* Main Headline */}
              <h1 className="text-xl sm:text-2xl lg:text-[1.625rem] xl:text-[1.875rem] font-bold text-slate-900 leading-[1.2] tracking-tight mb-2">
                Specialized Care for Swallowing & Communication
              </h1>
              
              <p className="text-sm lg:text-base text-slate-600 mb-4 leading-relaxed">
                Expert speech-language pathology services delivered with compassion, 
                right in your home across Greater Houston.
              </p>

              {/* Primary CTA */}
              <button
                onClick={() => setShowBookingModal(true)}
                className="inline-flex items-center justify-center gap-2 bg-teal-600 hover:bg-teal-700 text-white px-5 py-2.5 rounded-lg font-semibold text-sm transition-all shadow-md shadow-teal-600/20"
                data-testid="hero-book-btn"
              >
                <Calendar size={16} />
                Request an Evaluation
              </button>

              {/* Quick Contact - Desktop only */}
              <div className="hidden lg:flex items-center gap-2 mt-4">
                <a
                  href={`tel:${siteConfig.phoneFormatted || siteConfig.phone.replace(/\D/g, '')}`}
                  className="inline-flex items-center gap-2 text-slate-600 hover:text-teal-700 transition-colors text-sm"
                  data-testid="hero-call-btn"
                >
                  <Phone size={14} className="text-teal-600" />
                  <span className="font-medium">{siteConfig.phone}</span>
                </a>
              </div>
            </div>

            {/* Right: Video Showcase (65%) */}
            <div className="lg:col-span-7">
              {/* Video Container - Tight, fills column */}
              <div 
                className="relative w-full rounded-lg overflow-hidden shadow-lg"
                style={{ 
                  aspectRatio: '16/9',
                  backgroundColor: '#f1f5f9'
                }}
              >
                {/* Poster/Fallback */}
                {(prefersReducedMotion || !videoLoaded) && (
                  <div 
                    className="absolute inset-0"
                    style={{
                      backgroundImage: `url('/images/hero-poster.svg')`,
                      backgroundSize: 'contain',
                      backgroundPosition: 'center',
                      backgroundRepeat: 'no-repeat'
                    }}
                  />
                )}
                
                {/* Video Element */}
                {!prefersReducedMotion && (
                  <video
                    className={`w-full h-full object-contain transition-opacity duration-500 ${videoLoaded ? 'opacity-100' : 'opacity-0'}`}
                    autoPlay
                    muted
                    loop
                    playsInline
                    preload="auto"
                    poster="/images/hero-poster.svg"
                    onLoadedData={() => setVideoLoaded(true)}
                    onError={() => setVideoLoaded(false)}
                    aria-hidden="true"
                    data-testid="hero-video"
                  >
                    <source src="/videos/hero-background.webm" type="video/webm" />
                    <source src="/videos/hero-background.mp4" type="video/mp4" />
                  </video>
                )}
              </div>
              
              {/* Mobile phone link */}
              <div className="lg:hidden mt-3 text-center">
                <a
                  href={`tel:${siteConfig.phoneFormatted || siteConfig.phone.replace(/\D/g, '')}`}
                  className="inline-flex items-center gap-2 text-slate-600 hover:text-teal-700 transition-colors text-sm"
                  data-testid="hero-call-btn-mobile"
                >
                  <Phone size={14} className="text-teal-600" />
                  <span className="font-medium">{siteConfig.phone}</span>
                </a>
              </div>
            </div>
          </div>
        </div>
      </section>

      {/* Credibility Strip - Tight spacing */}
      <section className="py-3 bg-white border-y border-slate-200">
        <div className="container">
          <div className="flex flex-wrap items-center justify-center gap-4 md:gap-8 text-slate-700 text-sm">
            <div className="flex items-center gap-1.5">
              <Award size={16} className="text-teal-600" />
              <span className="font-medium">{verifiedStats.yearsExperience} Years Experience</span>
            </div>
            <div className="hidden sm:block w-px h-4 bg-slate-300" />
            <div className="flex items-center gap-1.5">
              <CheckCircle size={16} className="text-teal-600" />
              <span className="font-medium">{verifiedStats.certification} / ASHA</span>
            </div>
            <div className="hidden sm:block w-px h-4 bg-slate-300" />
            <div className="flex items-center gap-1.5">
              <Shield size={16} className="text-teal-600" />
              <span className="font-medium">Insurance Accepted</span>
            </div>
            <div className="hidden md:block w-px h-4 bg-slate-300" />
            <div className="hidden md:flex items-center gap-1.5">
              <Heart size={16} className="text-teal-600" />
              <span className="font-medium">Mobile In-Home Care</span>
            </div>
          </div>
        </div>
      </section>

      {/* How Can We Help - 3 Pathways */}
      <section className="py-16 md:py-24 bg-slate-50">
        <div className="container">
          <div className="text-center mb-12">
            <h2 className="text-3xl md:text-4xl font-bold text-slate-900 mb-4">How Can We Help You?</h2>
            <p className="text-lg text-slate-600 max-w-2xl mx-auto">
              Select the option that best describes you to find your next step.
            </p>
          </div>

          <div className="grid md:grid-cols-3 gap-6 max-w-5xl mx-auto">
            {/* Patient Pathway */}
            <Link
              to="/start-here"
              className="group bg-white rounded-2xl p-8 border border-slate-200 hover:border-teal-300 hover:shadow-xl transition-all duration-300"
              data-testid="pathway-patient"
            >
              <div className="w-14 h-14 bg-teal-100 rounded-xl flex items-center justify-center mb-5 group-hover:bg-teal-200 transition-colors">
                <User size={28} className="text-teal-700" />
              </div>
              <h3 className="text-xl font-semibold text-slate-900 mb-2">I Need Therapy</h3>
              <p className="text-slate-600 mb-4">
                You're experiencing swallowing difficulties, speech changes, or communication challenges.
              </p>
              <span className="inline-flex items-center gap-2 text-teal-700 font-semibold group-hover:gap-3 transition-all">
                Start Here <ArrowRight size={18} />
              </span>
            </Link>

            {/* Caregiver Pathway */}
            <Link
              to="/resources"
              className="group bg-white rounded-2xl p-8 border border-slate-200 hover:border-amber-300 hover:shadow-xl transition-all duration-300"
              data-testid="pathway-caregiver"
            >
              <div className="w-14 h-14 bg-amber-100 rounded-xl flex items-center justify-center mb-5 group-hover:bg-amber-200 transition-colors">
                <Users size={28} className="text-amber-700" />
              </div>
              <h3 className="text-xl font-semibold text-slate-900 mb-2">I'm a Caregiver</h3>
              <p className="text-slate-600 mb-4">
                You're helping a family member or loved one navigate their care and recovery.
              </p>
              <span className="inline-flex items-center gap-2 text-amber-700 font-semibold group-hover:gap-3 transition-all">
                Resources & Support <ArrowRight size={18} />
              </span>
            </Link>

            {/* Provider Pathway */}
            <Link
              to="/provider-referrals"
              className="group bg-white rounded-2xl p-8 border border-slate-200 hover:border-slate-400 hover:shadow-xl transition-all duration-300"
              data-testid="pathway-provider"
            >
              <div className="w-14 h-14 bg-slate-200 rounded-xl flex items-center justify-center mb-5 group-hover:bg-slate-300 transition-colors">
                <Stethoscope size={28} className="text-slate-700" />
              </div>
              <h3 className="text-xl font-semibold text-slate-900 mb-2">I'm a Healthcare Provider</h3>
              <p className="text-slate-600 mb-4">
                You want to refer a patient for evaluation or therapy services.
              </p>
              <span className="inline-flex items-center gap-2 text-slate-700 font-semibold group-hover:gap-3 transition-all">
                Refer a Patient <ArrowRight size={18} />
              </span>
            </Link>
          </div>

          {/* Quick Actions */}
          <div className="flex flex-wrap justify-center gap-4 mt-12">
            <Link to="/insurance-info" className="inline-flex items-center gap-2 px-5 py-2.5 bg-white border border-slate-200 rounded-full text-sm font-medium text-slate-700 hover:bg-slate-50 transition-colors">
              <Shield size={16} className="text-teal-600" />
              Insurance Information
            </Link>
            <Link to="/contact" className="inline-flex items-center gap-2 px-5 py-2.5 bg-white border border-slate-200 rounded-full text-sm font-medium text-slate-700 hover:bg-slate-50 transition-colors">
              <Phone size={16} className="text-teal-600" />
              Contact Us
            </Link>
            {simplePractice.enabled && simplePractice.clientPortalUrl ? (
              <a 
                href={simplePractice.clientPortalUrl}
                target="_blank"
                rel="noopener noreferrer"
                className="inline-flex items-center gap-2 px-5 py-2.5 bg-amber-500 hover:bg-amber-600 text-white rounded-full text-sm font-medium transition-colors"
              >
                <ExternalLink size={16} />
                Patient Portal
              </a>
            ) : (
              <span className="inline-flex items-center gap-2 px-5 py-2.5 bg-slate-100 border border-slate-200 rounded-full text-sm font-medium text-slate-500">
                <ExternalLink size={16} />
                Patient Portal: Coming Soon
              </span>
            )}
          </div>
        </div>
      </section>

      {/* Meet Your Clinician - Large Editorial Section */}
      <section className="py-16 md:py-24 bg-white">
        <div className="container">
          <div className="grid lg:grid-cols-2 gap-12 lg:gap-16 items-center max-w-6xl mx-auto">
            {/* Clinician Photo - Large Editorial Treatment */}
            <div className="order-2 lg:order-1">
              <div className="relative">
                {clinician.photo ? (
                  <img 
                    src={clinician.photo}
                    alt={`${clinician.name}, ${clinician.credentials}, Speech-Language Pathologist`}
                    className="w-full rounded-2xl shadow-2xl"
                    onError={(e) => {
                      e.target.style.display = 'none';
                      e.target.nextSibling.style.display = 'flex';
                    }}
                  />
                ) : null}
                {/* Placeholder if image not available */}
                <div 
                  className={`aspect-[4/5] bg-gradient-to-br from-teal-50 to-slate-100 rounded-2xl shadow-xl flex flex-col items-center justify-center ${clinician.photo ? 'hidden' : ''}`}
                >
                  <div className="w-32 h-32 bg-teal-100 rounded-full flex items-center justify-center mb-6">
                    <User size={64} className="text-teal-600" />
                  </div>
                  <span className="text-teal-600 font-medium">Photo Coming Soon</span>
                </div>
                
                {/* Credentials Badge */}
                <div className="absolute -bottom-4 -right-4 md:bottom-6 md:right-6 bg-white rounded-xl shadow-lg p-4 border border-slate-100">
                  <div className="flex items-center gap-3">
                    <div className="w-10 h-10 bg-teal-100 rounded-lg flex items-center justify-center">
                      <Award size={20} className="text-teal-700" />
                    </div>
                    <div>
                      <span className="block text-sm font-bold text-slate-900">{verifiedStats.yearsExperience} Years</span>
                      <span className="text-xs text-slate-500">Clinical Experience</span>
                    </div>
                  </div>
                </div>
              </div>
            </div>

            {/* Clinician Info */}
            <div className="order-1 lg:order-2">
              <span className="text-sm font-semibold text-teal-700 tracking-wide uppercase mb-4 block">
                Meet Your Clinician
              </span>
              <h2 className="text-3xl md:text-4xl font-bold text-slate-900 mb-2">
                {clinician.name}
              </h2>
              <p className="text-xl text-teal-700 font-medium mb-6">
                {clinician.credentials}
              </p>
              <p className="text-lg text-slate-600 mb-4">
                {clinician.title}
              </p>
              <p className="text-slate-600 leading-relaxed mb-8">
                {clinician.bio}
              </p>
              
              {/* Credentials List */}
              <div className="space-y-3 mb-8">
                {clinician.certifications.slice(0, 3).map((cert, index) => (
                  <div key={index} className="flex items-center gap-3">
                    <CheckCircle size={20} className="text-teal-600 flex-shrink-0" />
                    <span className="text-slate-700">{cert}</span>
                  </div>
                ))}
              </div>
              
              <Link 
                to="/about" 
                className="inline-flex items-center gap-2 text-teal-700 font-semibold hover:gap-3 transition-all"
                data-testid="meet-gilda-link"
              >
                Learn More About Gilda <ArrowRight size={18} />
              </Link>
            </div>
          </div>
        </div>
      </section>

      {/* Patient Experiences / Google Reviews */}
      <section className="py-16 md:py-24 bg-slate-50">
        <div className="container">
          <div className="max-w-4xl mx-auto text-center">
            <h2 className="text-3xl md:text-4xl font-bold text-slate-900 mb-4">
              {googleReviews.heading}
            </h2>
            <p className="text-lg text-slate-600 mb-10">
              {googleReviews.subheading}
            </p>

            {/* Google Reviews Card */}
            <div className="bg-white rounded-2xl shadow-lg border border-slate-200 p-8 md:p-12">
              <div className="flex items-center justify-center gap-1 mb-4">
                {[...Array(5)].map((_, i) => (
                  <Star key={i} size={28} fill="#D97706" className="text-amber-500" />
                ))}
              </div>
              
              {/* Verified Business Address */}
              {googleReviews.businessAddress && (
                <p className="text-sm text-slate-500 mb-6 flex items-center justify-center gap-1">
                  <MapPin size={14} className="text-teal-600" />
                  {googleReviews.businessAddress}
                </p>
              )}
              
              {googleReviews.reviewsUrl ? (
                <div className="space-y-4">
                  <a
                    href={googleReviews.reviewsUrl}
                    target="_blank"
                    rel="noopener noreferrer"
                    className="inline-flex items-center gap-2 bg-teal-700 hover:bg-teal-800 text-white px-6 py-3 rounded-xl font-semibold transition-colors"
                    data-testid="home-read-reviews-cta"
                  >
                    {googleReviews.ctaRead}
                    <ExternalLink size={18} />
                  </a>
                  <div className="block">
                    <a
                      href={googleReviews.reviewsUrl}
                      target="_blank"
                      rel="noopener noreferrer"
                      className="inline-flex items-center gap-2 text-teal-700 font-medium hover:text-teal-800 transition-colors"
                      data-testid="home-leave-review-cta"
                    >
                      <Star size={16} className="fill-current" />
                      {googleReviews.ctaLeave}
                      <ArrowRight size={16} />
                    </a>
                  </div>
                </div>
              ) : (
                <div className="text-center">
                  <p className="text-slate-500 mb-4">Google Business Profile coming soon</p>
                  <p className="text-sm text-slate-400">
                    Contact us directly to share your experience
                  </p>
                  <a
                    href={`tel:${siteConfig.phone}`}
                    className="inline-flex items-center gap-2 mt-4 text-teal-700 font-medium"
                  >
                    <Phone size={16} />
                    {siteConfig.phone}
                  </a>
                </div>
              )}
            </div>

            {/* Note about testimonials */}
            <p className="text-sm text-slate-500 mt-6">
              Individual results may vary. Patient testimonials reflect personal experiences.
            </p>
          </div>
        </div>
      </section>

      {/* Final CTA Section */}
      <section className="py-16 md:py-24 bg-teal-700">
        <div className="container">
          <div className="max-w-3xl mx-auto text-center text-white">
            <h2 className="text-3xl md:text-4xl font-bold mb-4">
              Ready to Take the First Step?
            </h2>
            <p className="text-lg text-teal-100 mb-10">
              Schedule a consultation today and let us help you or your loved one 
              on the path to better swallowing and communication.
            </p>
            <div className="flex flex-col sm:flex-row items-center justify-center gap-4">
              <button
                onClick={() => setShowBookingModal(true)}
                className="w-full sm:w-auto inline-flex items-center justify-center gap-2 bg-white text-teal-700 hover:bg-teal-50 px-8 py-4 rounded-xl font-semibold text-lg transition-colors"
                data-testid="cta-book-btn"
              >
                <Calendar size={22} />
                Book an Evaluation
              </button>
              <a
                href={`tel:${siteConfig.phone}`}
                className="w-full sm:w-auto inline-flex items-center justify-center gap-2 bg-teal-600 hover:bg-teal-500 text-white px-8 py-4 rounded-xl font-semibold text-lg transition-colors border border-teal-500"
                data-testid="cta-call-btn"
              >
                <Phone size={22} />
                Call Us
              </a>
            </div>
          </div>
        </div>
      </section>

      <BookingModal isOpen={showBookingModal} onClose={() => setShowBookingModal(false)} />
    </div>
  );
};

export default HomePage;
